import { useEffect, useState } from "react";
import ScoreBadge from "@/components/feature/ScoreBadge";
import StatusBadge from "@/components/feature/StatusBadge";

export type Prospect = {
  id: string;
  name: string;
  photo: string;
  username: string;
  platform: string;
  followers: number;
  niche: string;
  reason: string;
  score: number;
  /** Enlace público del perfil en su red (se abre en otra pestaña). */
  profileUrl?: string;
  /** Motivos del score calculado (audiencia, relevancia, ubicación...). */
  scoreNotes?: string[];
  /** Código del link único de este prospecto al webinar. */
  trackCode?: string;
  linkClicks?: number;
  linkRegistered?: boolean;
  linkLastClickAt?: string;
  replyCount?: number;
  lastReplyAt?: string;
  status: string;
  nextAction: string;
  followUp: string;
  /** Requiere que una persona intervenga (el agente no pudo, o te respondió). */
  needsHuman?: boolean;
  needsHumanReason?: string;
  /** Quién atiende la conversación ahora: el agente de IA o una persona. */
  handledBy?: "ai" | "human";
};

type ProspectCardProps = {
  prospect: Prospect;
  /** Texto del opener ya generado/guardado para este prospecto, o null si aún no existe. */
  opener?: string | null;
  approved: boolean;
  sent: boolean;
  /** true mientras hay una llamada a Supabase en curso para ESTE prospecto — desactiva los botones para evitar doble clic / escrituras duplicadas. */
  busy?: boolean;
  onView: (p: Prospect) => void;
  onGenerate: (p: Prospect) => void;
  onApprove: (p: Prospect) => void;
  onSend: (p: Prospect) => void;
  /** Abre el perfil en otra pestaña con el mensaje ya copiado. */
  onOpenWrite?: (p: Prospect) => void;
  /** Abre la conversación para registrar lo que ella respondió. */
  onReply?: (p: Prospect) => void;
  /** Agrega el prospecto a la cola de envío. */
  onQueue?: (p: Prospect) => void;
  queued?: boolean;
};

const platformIcon: Record<string, string> = {
  Instagram: "ri-instagram-line",
  TikTok: "ri-tiktok-line",
  Facebook: "ri-facebook-circle-line",
  YouTube: "ri-youtube-line",
  LinkedIn: "ri-linkedin-line",
};

export function formatFollowers(n: number) {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(".0", "")}K`;
  return `${n}`;
}

export default function ProspectCard({
  prospect,
  opener = null,
  approved,
  sent,
  busy = false,
  onView,
  onGenerate,
  onApprove,
  onSend,
  onOpenWrite,
  onReply,
  onQueue,
  queued = false,
}: ProspectCardProps) {
  // Al tocar la caja del opener se expande para mostrarlo completo (en vez de
  // las 4 líneas recortadas); se colapsa de nuevo al volver a tocarla.
  const [expanded, setExpanded] = useState(false);

  // Si el opener cambia (se generó uno nuevo, se mejoró, etc.) arrancamos
  // siempre recortado, para no dejar una tarjeta "expandida" de un texto viejo.
  useEffect(() => {
    setExpanded(false);
  }, [opener]);

  return (
    <article className="bg-background-100 border border-background-200 rounded-2xl p-4 md:p-5">
      <div className="flex items-start gap-4">
        <img
          src={prospect.photo}
          alt={`Foto de ${prospect.name}`}
          className="w-14 h-14 rounded-2xl object-cover object-top border border-background-200 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-heading font-bold text-foreground-950 truncate">{prospect.name}</h3>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-600 mt-0.5">
                <span className="inline-flex items-center gap-1">
                  <i className={`${platformIcon[prospect.platform] ?? "ri-user-line"}`}></i>
                  {prospect.username}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-group-line"></i> {formatFollowers(prospect.followers)}
                </span>
              </div>
            </div>
            <ScoreBadge score={prospect.score} notes={prospect.scoreNotes} />
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-background-100 text-foreground-700 px-2.5 py-0.5 rounded-full">
              <i className="ri-price-tag-3-line"></i> {prospect.niche}
            </span>
            <StatusBadge status={prospect.status} />
            {prospect.replyCount ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-secondary-500/10 text-secondary-700 px-2.5 py-0.5 rounded-full">
                <i className="ri-chat-3-line"></i> {prospect.replyCount === 1 ? "1 respuesta" : `${prospect.replyCount} respuestas`}
              </span>
            ) : null}
            {prospect.linkRegistered ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-primary-500/15 text-primary-700 px-2.5 py-0.5 rounded-full">
                <i className="ri-user-add-line"></i> Se registró
              </span>
            ) : prospect.linkClicks ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-accent-500/10 text-accent-700 px-2.5 py-0.5 rounded-full">
                <i className="ri-cursor-line"></i> Abrió el link{prospect.linkClicks > 1 ? ` (${prospect.linkClicks})` : ""}
              </span>
            ) : null}
          </div>

          <p className="text-sm text-foreground-600 mt-3">
            <span className="inline-flex items-center gap-1 font-semibold text-accent-700 text-xs mr-1">
              <i className="ri-sparkling-2-fill"></i> Motivo IA:
            </span>
            {prospect.reason}
          </p>

          <div className="mt-2 flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-focus-3-line text-primary-500"></i>
            <span className="truncate">
              {prospect.nextAction} · {prospect.followUp}
            </span>
          </div>
        </div>
      </div>

      {opener ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-3 w-full text-left rounded-xl bg-accent-500/10 border border-accent-500/25 p-3 cursor-pointer hover:bg-accent-500/15 transition-colors"
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-accent-700">
              <i className="ri-magic-line"></i> Opener generado por IA
            </span>
            <i
              className={`ri-arrow-down-s-line text-accent-700 text-base transition-transform ${expanded ? "rotate-180" : ""}`}
              aria-hidden="true"
            ></i>
          </div>
          <p className={`text-sm text-foreground-800 whitespace-pre-line ${expanded ? "" : "line-clamp-4"}`}>{opener}</p>
          <span className="block mt-1.5 text-[11px] font-medium text-accent-600">
            {expanded ? "Toca para ver menos" : "Toca para ver completo"}
          </span>
        </button>
      ) : (
        <div className="mt-3 rounded-xl bg-background-50 border border-dashed border-background-300 p-3 text-center">
          <p className="text-xs text-foreground-500">Aún no hay opener generado para {prospect.name}.</p>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <button
          onClick={() => onView(prospect)}
          disabled={busy}
          className="py-2.5 rounded-xl border border-background-300 text-foreground-800 font-heading font-semibold text-xs inline-flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap hover:border-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="ri-user-search-line"></i> Ver perfil
        </button>
        <button
          onClick={() => onGenerate(prospect)}
          disabled={busy}
          className="py-2.5 rounded-xl bg-accent-500/10 text-accent-700 border border-accent-500/30 font-heading font-semibold text-xs inline-flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap hover:bg-accent-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className={busy ? "ri-loader-4-line animate-spin" : "ri-magic-line"}></i> {opener ? "Mejorar opener" : "Generar opener"}
        </button>
        <button
          onClick={() => onApprove(prospect)}
          disabled={approved || busy || !opener}
          title={!opener ? "Primero genera el opener" : undefined}
          className={[
            "py-2.5 rounded-xl font-heading font-semibold text-xs inline-flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
            approved
              ? "bg-primary-500 text-background-950"
              : "bg-primary-500/10 text-primary-700 border border-primary-500/30 hover:bg-primary-500/20",
          ].join(" ")}
        >
          <i className={approved ? "ri-check-double-line" : "ri-check-line"}></i>
          {approved ? "Aprobado" : "Aprobar"}
        </button>
        <button
          onClick={() => onSend(prospect)}
          disabled={sent || busy || !approved}
          title={!approved ? "Primero aprueba el opener" : undefined}
          className={[
            "py-2.5 rounded-xl font-heading font-semibold text-xs inline-flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
            sent
              ? "bg-secondary-500 text-white"
              : "bg-secondary-500/10 text-secondary-700 border border-secondary-500/30 hover:bg-secondary-500/20",
          ].join(" ")}
        >
          <i className={sent ? "ri-send-plane-fill" : "ri-send-plane-line"}></i>
          {sent ? "Enviado" : "Enviar"}
        </button>
      </div>
      {onOpenWrite ? (
        <button
          onClick={() => onOpenWrite(prospect)}
          disabled={busy || !prospect.profileUrl}
          title={prospect.profileUrl ? "Abre su perfil y copia el mensaje" : "Este prospecto no tiene enlace de perfil"}
          className="mt-2 w-full py-3 rounded-xl bg-primary-500 text-background-950 font-heading font-bold text-sm inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="ri-chat-new-line"></i> Abrir y escribir
        </button>
      ) : null}
      {onQueue && !sent ? (
        <button
          onClick={() => onQueue(prospect)}
          disabled={queued || !prospect.profileUrl}
          className="mt-2 w-full py-2 rounded-xl border border-dashed border-background-300 text-foreground-700 font-heading font-semibold text-xs inline-flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap hover:border-primary-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i className={queued ? "ri-check-line" : "ri-play-list-add-line"}></i> {queued ? "En la cola de envío" : "Añadir a la cola de envío"}
        </button>
      ) : null}
      {onReply ? (
        <button
          onClick={() => onReply(prospect)}
          className="mt-2 w-full py-2.5 rounded-xl border border-primary-500/40 text-primary-700 bg-primary-500/5 font-heading font-semibold text-sm inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap hover:bg-primary-500/15 transition-colors"
        >
          <i className="ri-chat-check-line"></i> {prospect.replyCount ? "Ver conversación" : "Ella respondió"}
        </button>
      ) : null}
    </article>
  );
}
